<script setup lang="ts" generic="IncludePrimitive extends boolean = true, IncludeRelation extends boolean = true, Multiple extends boolean = false">
import type { CollectionFieldSelectPresentationProps, ModelValueBase } from './CollectionFieldSelect.vue';
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';

type ModelValue = ModelValueBase<IncludePrimitive, IncludeRelation, Multiple>;

interface CollectionFieldSelectDropdownProps<Multiple extends boolean> extends CollectionFieldSelectPresentationProps<Multiple> {
	clear?: boolean;
}

const props = withDefaults(defineProps<CollectionFieldSelectDropdownProps<Multiple>>(), {
	clear: true,
});

const emit = defineEmits<{ clear: [] }>();

defineSlots<{
	default: () => void;
}>();

const modelValue = defineModel<ModelValue>({ required: true });

const { t } = useI18n();

const selectionText = computed(() => {
	const selectionCount = Array.isArray(modelValue.value) ? modelValue.value.length : 0;
	const selectionCountText = t('collection_field_select.n_fields_selected', selectionCount); ;

	if (selectionCount === props.items.length) {
		return t('collection_field_select.all_fields_selected');
	}
	else if (selectionCount <= 3 && Array.isArray(modelValue.value)) {
		// small enough count to show the field names and truncate them
		return modelValue.value
			.map((fieldId) => props.items.find((field) => field.id === fieldId)?.label)
			.filter(Boolean)
			.join(', ') || selectionCountText;
	}
	else {
		return selectionCountText;
	}
});
</script>

<template>
	<DSelectMenu
		v-model="modelValue as NonNullable<ModelValue> | undefined"
		value-key="id"
		spellcheck="false"
		:filter-fields="['apiName', 'label']"
		:items
		:multiple
		:clear
		:content="{ align: 'start' }"
		@clear="emit('clear')"
	>
		<template v-if="multiple || !!$slots.default" #default>
			<slot>
				<span class="truncate">
					{{ selectionText }}
				</span>
			</slot>
		</template>
	</DSelectMenu>
</template>
